/**
 * Forensic Sanitation Utility for Hajez Sovereign Business Nodes
 *
 * Provides a single source of truth for cleaning service strings,
 * handling legacy "dirty" data, and preventing future architectural drift.
 */

/**
 * Surgically cleans service type strings or arrays.
 * - Splits by commas
 * - Trims whitespace and hidden Unicode markers (Zero-Width, Non-Breaking)
 * - Filters out empty/ghost entries
 * - Re-joins with standardized spacing
 * - Force-wipes leading commas and rogue punctuation
 */
export const sanitizeServiceType = (
  services: string | unknown[] | null | undefined,
  language: "ar" | "en" = "en",
): string => {
  if (!services) return "";

  // Step 1: Standardize to string
  const raw = Array.isArray(services)
    ? services
        .map((s) => {
          if (typeof s === "object" && s !== null && "name" in s) {
            const service = s as { name: string; name_ar?: string };
            return language === "ar" && service.name_ar
              ? service.name_ar
              : service.name;
          }
          return String(s);
        })
        .join(", ")
    : String(services);

  // Step 2: Split-Trim-Filter-Join Pipeline
  const cleaned = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");

  // Step 3: Forensic Unicode Wipe
  // Cleans: Leading commas, Standard spaces, Tabs,
  // Non-Breaking Spaces (\u00A0), Zero-Width Spaces (\u200B-D),
  // and other invisible layout-breaking markers.
  return cleaned.replace(
    /^([\s,\u00A0\u2060\uFEFF]|\u200B|\u200C|\u200D)+/,
    "",
  );
};

/**
 * Normalized Phone Number Utility
 *
 * Standardizes diverse phone inputs (079..., 79..., 96279...)
 * into the forensic Meta format (96279...).
 */
export const normalizePhoneNumber = (
  phone: string | null | undefined,
): string | null => {
  if (!phone) return null;

  // Step 1: Map Arabic-Indic digits (٠-٩) to ASCII before stripping —
  // otherwise \D treats them as non-digits and deletes them, corrupting the number.
  const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
  const asciiPhone = phone.replace(
    /[٠-٩]/g,
    (d) => `${arabicDigits.indexOf(d)}`,
  );
  const cleaned = asciiPhone.replace(/\D/g, "");

  // Step 2: Protocol Mapping (Jordan Context)
  // - 07(7,8,9)XXXXXXXX -> 9627(7,8,9)XXXXXXXX (10 digits)
  if (/^07[789]\d{7}$/.test(cleaned)) {
    return "962" + cleaned.substring(1);
  }

  // - 7(7,8,9)XXXXXXXX -> 9627(7,8,9)XXXXXXXX (9 digits)
  if (/^7[789]\d{7}$/.test(cleaned)) {
    return "962" + cleaned;
  }

  // - 9627(7,8,9)XXXXXXXX -> 9627(7,8,9)XXXXXXXX (12 digits)
  if (/^9627[789]\d{7}$/.test(cleaned)) {
    return cleaned;
  }

  // Fallback if already 962... but with edge formatting
  if (cleaned.startsWith("962") && cleaned.length >= 12) {
    return cleaned;
  }

  return null; // Forensic rejection of invalid numbers
};
